import { useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/shared/utils'
import { EyeOff, FileText, Link2, MapPin, Route, Users } from '@/ui/icons'

const SHARING_PERMS = [
  { key: 'gps' as const,       label: 'GPS coordinates',      Icon: MapPin   },
  { key: 'campsites' as const, label: 'Campsite coordinates', Icon: MapPin   },
  { key: 'route' as const,     label: 'Route geometry',       Icon: Route    },
  { key: 'crew' as const,      label: 'Crew list',            Icon: Users    },
  { key: 'notes' as const,     label: 'Notes & details',      Icon: FileText },
]

type SharingPermKey = 'gps' | 'campsites' | 'route' | 'crew' | 'notes'

function GuestPreviewField({
  label,
  value,
  hiddenValue,
  visible,
  reducedMotion,
}: {
  label: string
  value: string
  hiddenValue: string
  visible: boolean
  reducedMotion: boolean
}) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.15em] text-earth-400 dark:text-earth-500">{label}</p>
      <motion.div
        key={String(visible)}
        initial={reducedMotion ? false : { opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="mt-0.5"
      >
        {visible ? (
          <p className="text-sm text-earth-800 dark:text-earth-200">{value}</p>
        ) : (
          <p className="flex items-center gap-1.5 text-sm italic text-earth-300 dark:text-earth-600">
            <EyeOff className="h-3 w-3 shrink-0" />
            {hiddenValue}
          </p>
        )}
      </motion.div>
    </div>
  )
}

export function SharingPermissionsDemo() {
  const [perms, setPerms] = useState<Record<SharingPermKey, boolean>>({
    gps: false,
    campsites: false,
    route: true,
    crew: false,
    notes: true,
  })
  const reducedMotion = useReducedMotion() ?? false
  const toggle = (key: SharingPermKey) => setPerms((p) => ({ ...p, [key]: !p[key] }))

  return (
    <div className="overflow-hidden rounded-2xl border border-earth-200 bg-white shadow-xl dark:border-earth-700 dark:bg-earth-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-earth-100 px-5 py-3.5 dark:border-earth-800">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-earth-500 dark:text-earth-400">
            Share link settings
          </p>
          <p className="text-sm font-semibold text-earth-900 dark:text-earth-50">Mojave Spring Run</p>
        </div>
        <span className="flex items-center gap-1.5 rounded-full bg-adventure-50 px-2.5 py-1 text-[11px] font-medium text-adventure-700 dark:bg-adventure-900/30 dark:text-adventure-300">
          <Link2 className="h-3 w-3" />
          Open link
        </span>
      </div>

      {/* Permission toggles */}
      <div className="space-y-2.5 border-b border-earth-100 px-5 py-4 dark:border-earth-800">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-earth-400 dark:text-earth-500">
          What guests can see
        </p>
        {SHARING_PERMS.map(({ key, label, Icon }) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <Icon className="h-4 w-4 shrink-0 text-earth-400 dark:text-earth-500" />
              <span className="text-sm text-earth-700 dark:text-earth-300">{label}</span>
            </div>
            <button
              role="switch"
              aria-checked={perms[key]}
              aria-label={`Toggle ${label}`}
              onClick={() => toggle(key)}
              className={cn(
                'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200',
                perms[key] ? 'bg-adventure-500' : 'bg-earth-200 dark:bg-earth-700',
              )}
            >
              <motion.span
                className="inline-block h-3.5 w-3.5 rounded-full bg-white shadow"
                animate={{ x: perms[key] ? 18 : 2 }}
                transition={
                  reducedMotion
                    ? { duration: 0 }
                    : { type: 'spring', stiffness: 500, damping: 30 }
                }
              />
            </button>
          </div>
        ))}
      </div>

      {/* Guest preview */}
      <div className="bg-earth-50 px-5 py-4 dark:bg-earth-950">
        <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-earth-400 dark:text-earth-500">
          Guest sees
        </p>
        <div className="space-y-3 rounded-xl border border-earth-200 bg-white p-4 dark:border-earth-700 dark:bg-earth-900">
          <div>
            <p className="text-sm font-semibold text-earth-900 dark:text-earth-50">Mojave Spring Run</p>
            <p className="text-[11px] text-earth-500 dark:text-earth-400">Apr 12–15 · 4 days</p>
          </div>
          <div className="space-y-2 pt-1">
            <GuestPreviewField
              label="Start location"
              value="34.052°N 116.220°W · Barstow"
              hiddenValue="Location hidden"
              visible={perms.gps}
              reducedMotion={reducedMotion}
            />
            <GuestPreviewField
              label="Campsite coordinates"
              value="34.127°N 115.844°W · Hidden Valley"
              hiddenValue="Campsite pins hidden"
              visible={perms.campsites}
              reducedMotion={reducedMotion}
            />
            <GuestPreviewField
              label="Route"
              value="247 mi · Mojave → Twentynine Palms"
              hiddenValue="Route hidden"
              visible={perms.route}
              reducedMotion={reducedMotion}
            />
            <GuestPreviewField
              label="Crew"
              value="Alex M., Jordan T., Sam K., +1"
              hiddenValue="4 members · names hidden"
              visible={perms.crew}
              reducedMotion={reducedMotion}
            />
            <GuestPreviewField
              label="Notes"
              value="CB ch. 19 · Sand ladders req."
              hiddenValue="Notes hidden"
              visible={perms.notes}
              reducedMotion={reducedMotion}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
